/** @jsx jsx */
import { jsx } from "theme-ui"
import { Global } from "@emotion/core"
import { ThemeProvider } from "theme-ui"
import i18next from "i18next"
import LanguageDetector from "i18next-browser-languagedetector"
import { initReactI18next } from "react-i18next"
import { SkipNavLink, SkipNavContent } from "@reach/skip-nav"
import { useStaticQuery, graphql } from "gatsby"

import "typeface-montserrat"
import "modern-normalize/modern-normalize.css"
import "@reach/skip-nav/styles.css"

import SEO from "./molecules/SEO"
import Header from "./molecules/Header"
import Footer from "./molecules/Footer"
import LanguageSwitcher from "./molecules/LanguageSwitcher"
import {
  isDefaultLng,
  getResolvedLocale,
  getDataForLocale,
} from "../utils/i18n"
import locales from "../../../locales"
import theme from "../theme"

i18next
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    initImmediate: false,
    whitelist: locales.map(locale => locale.config.code),
    nonExplicitWhitelist: true,
    resources: locales.reduce(
      (acc, locale) => ({
        ...acc,
        [locale.config.code]: locale,
      }),
      {}
    ),
    // debug: true,
    lowerCaseLng: true,
    detection: {
      order: ["path", "localStorage", "navigator"],
    },
    fallbackLng: "en",
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
  })

let isRedirecting = false
const resolvedLocale = getResolvedLocale(i18next)

if (
  typeof window !== "undefined" &&
  window.location.pathname === "/" &&
  !isDefaultLng(resolvedLocale.config.code)
) {
  isRedirecting = true
  window.location = `/${resolvedLocale.config.code}`
}

function Root({ children, pageContext, location }) {
  const data = useStaticQuery(graphql`
    query Commons {
      sanityMisc {
        moreProjects {
          ...StringAllLanguages
        }
        photoCredit {
          ...StringAllLanguages
        }
      }
    }
  `)
  const localizedData = getDataForLocale(data, pageContext.locale)

  // Load commons data in i18next
  i18next.addResourceBundle(pageContext.locale, "translation", {
    commons: localizedData.sanityMisc,
  })
  i18next.changeLanguage(pageContext.locale)

  return isRedirecting ? null : (
    <ThemeProvider theme={theme}>
      <Global
        styles={theme => ({
          html: {
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            fontSize: "62.5%",
            direction: i18next.dir(),
          },
          body: {
            fontSize: theme.fontSizes.body,
            fontFamily: theme.fonts.body,
            fontWeight: theme.fontWeights.body,
            lineHeight: theme.lineHeights.body,
            color: theme.colors.body,
          },
          "b, strong": {
            fontWeight: "bold",
          },
          a: {
            textDecoration: "none",
            color: "inherit",
          },
          "button, a": {
            transition: "all 150ms",
          },
          img: {
            maxWidth: "100%",
          },
          "[hidden]": {
            display: "none!important",
          },
          "input, textarea": {
            width: "100%",
            maxWidth: "100%",
            border: "1px solid",
            borderColor: "gray",
            background: "#f9f9f9",
            borderRadius: 8,
            padding: theme.space[3],
            "&:focus": {
              outline: "none",
              borderColor: theme.colors.primary,
              background: "white",
            },
          },
          ".overflow-auto": {
            overflow: "auto",
          },
          ".overflow-hidden": {
            overflow: "hidden",
          },
        })}
      />
      <SEO
        bodyAttributes={{
          className: "overflow-auto",
        }}
      />
      <SkipNavLink />
      <div sx={{ display: ["none", "block"] }}>
        <LanguageSwitcher location={location} />
      </div>
      <Header location={location} />
      <SkipNavContent />
      <main>{children}</main>
      <Footer />
    </ThemeProvider>
  )
}

export default Root
